<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import {
        useQueryGetBackOfficeTenants,
        useMutationUpdateOrgTree,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    const successDialogStore = useSuccessDialog();
    const { setFocus } = useElementFocus();

    onMounted(() => {
        setFocus('#update-org-tree-tenant');
    });

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const tenants = computed(() => tenantsData.value ?? []);

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const messages = ref<string[]>([]);

    const tenantOptions = computed<Option[]>(() =>
        tenants.value.map((t) => ({ name: t.name, value: t.id }))
    );

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
        messages.value = [];
    };

    const onTenantClear = () => {
        selectedTenantId.value = '';
        messages.value = [];
    };

    const { mutateAsync: updateOrgTreeMutation, isPending } = useMutationUpdateOrgTree();

    const submit = async () => {
        if (!selectedTenantId.value) return;
        const response = await updateOrgTreeMutation({ tenantId: selectedTenantId.value });
        messages.value = response.errors;
        if (response.errors.length === 0) {
            successDialogStore.openDialog({
                message: __('back-office-update-org-tree-success'),
            });
        }
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-update-org-tree') }}</h1>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <EchoInfoBox class="mb-6">
                    {{ __('back-office-update-org-tree-info') }}
                </EchoInfoBox>
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="update-org-tree-tenant"
                        :label="__('back-office-update-org-tree-select-tenant')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />
                </FormGroup>
                <div class="mt-8 flex flex-col gap-3 w-fit">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!selectedTenantId"
                        :loading="isPending"
                    >
                        {{ __('back-office-update-org-tree-submit') }}
                    </EchoButton>
                </div>
                <div v-if="messages.length > 0" class="mt-6">
                    <p class="text-body">{{ __('back-office-update-org-tree-messages') }}</p>
                    <ul class="mt-2 list-disc pl-5 text-body">
                        <li v-for="message in messages" :key="message">{{ message }}</li>
                    </ul>
                </div>
            </form>
        </template>
    </SettingsPageLayout>
</template>
